// Import wizard — the required-column guard on the mapping step, and
// the busy state of Commit on both the mapping and review steps.
//
// The wizard's one hard rule is that some column has to become First
// name or Full name. Left to the server, breaking it costs a round
// trip; caught here, it is a sentence above the columns and the cursor
// back in the mapping.
//
// The server still enforces it — this is a courtesy, and a form posted
// with scripting off must not commit a nameless import. The two share
// the message through data-required-message so they cannot drift.
//
// Targets: [data-import-mapping], [data-import-review].

import { counted, grouped } from "./count_phrase.js";
import { onceGuard } from "./bind_once.js";

const firstBind = onceGuard();

const COMMITTING_MESSAGE = "Importing your prospects. This can take a minute.";

// setCommitting shows a commit under way: Commit disabled and its
// label swapped (in CSS, off :disabled), Cancel and Back inert because
// leaving no longer stops the commit, the review's choices inert
// because the form has gone, and the wait announced.
function setCommitting(form, committing) {
  form.querySelectorAll("[data-import-commit]").forEach((button) => {
    button.disabled = committing;
  });
  form
    .querySelectorAll(
      "[data-import-cancel], [data-import-back], [data-import-choices]",
    )
    .forEach((control) => {
      control.inert = committing;
    });
  const status = form.querySelector("[data-import-commit-status]");
  if (status) status.textContent = committing ? COMMITTING_MESSAGE : "";
}

// A page restored from the back/forward cache, or a Firefox reload that
// restores form state, keeps the state a submit left behind. One
// listener for the page, whichever wizard it holds now.
// A reload can also restore the review's boxes after it counted them.
window.addEventListener("pageshow", () => {
  document
    .querySelectorAll("[data-import-mapping], [data-import-review]")
    .forEach((form) => {
      setCommitting(form, false);
      form.dispatchEvent(new Event("change"));
    });
});

const rows = (n) => counted(n, "row", "rows");

// reviewCount words the total above Commit as importReviewCount in
// import_review.templ does.
function reviewCount(importing, total) {
  if (importing === 0) return "Choose at least one group to import.";
  if (importing === 1 && total === 1) return "Importing 1 row.";
  if (importing === 2 && total === 2) return "Importing both rows.";
  if (importing === total) return `Importing all ${rows(total)}.`;

  const left = total - importing;
  const leftOut =
    left === 1
      ? "The 1 you leave out is counted as skipped."
      : `The ${grouped(left)} you leave out are counted as skipped.`;

  return `Importing ${grouped(importing)} of ${rows(total)}. ${leftOut}`;
}

// initImportReview wires the review step: a running total as the agent
// toggles groups, no commit of zero rows (the server refuses one too),
// and Commit's busy state. Back submits a form of its own, so it never
// reaches this one.
export function initImportReview(root = document) {
  root.querySelectorAll("[data-import-review]").forEach((form) => {
    const note = form.querySelector("[data-import-review-count]");
    const commit = form.querySelector("[data-import-commit]");
    // Each group's label carries its row count; the box sits inside.
    const groups = [...form.querySelectorAll("label[data-count]")];
    if (!note || !commit) return;

    if (!firstBind(form)) return;

    const complete = Number(form.dataset.complete);
    const total = Number(form.dataset.importable);
    const importing = () =>
      groups.reduce(
        (n, group) =>
          group.querySelector("input[type=checkbox]")?.checked
            ? n + Number(group.dataset.count)
            : n,
        complete,
      );

    // aria-disabled rather than disabled: :disabled is Commit's busy
    // state, and a disabled button would read "Importing…".
    const update = () => {
      const n = importing();
      note.textContent = reviewCount(n, total);
      if (n === 0) commit.setAttribute("aria-disabled", "true");
      else commit.removeAttribute("aria-disabled");
    };

    form.addEventListener("change", update);
    update();

    form.addEventListener("submit", (event) => {
      if (importing() === 0) {
        event.preventDefault();

        return;
      }

      setCommitting(form, true);
    });
  });
}

export function initImportMapping(root = document) {
  root.querySelectorAll("[data-import-mapping]").forEach((form) => {
    const selects = [...form.querySelectorAll("[data-mapping-select]")];
    const error = form.querySelector("[data-mapping-error]");
    // Any one of these fields satisfies the rule.
    const required = form.dataset.requiredField?.split(" ");
    const message = form.dataset.requiredMessage;
    // Stamped only once every piece is present. A form that arrives
    // without its banner stays wireable by a later bootstrap pass
    // instead of being marked done and left inert. message is in the
    // guard too, or the banner reads "undefined".
    if (!selects.length || !error || !required || !message) return;

    if (!firstBind(form)) return;

    const mapped = () =>
      selects.some((select) => required.includes(select.value));

    const show = () => {
      error.textContent = message;
      error.hidden = false;
    };

    // Cleared on the way to being fixed rather than on submit, so the
    // agent sees the rule satisfied the moment they satisfy it.
    const clearWhenSatisfied = () => {
      if (mapped()) error.hidden = true;
    };

    form.addEventListener("submit", (event) => {
      if (mapped()) {
        // A second click would send a second commit, and the browser
        // would show that answer instead of this one.
        setCommitting(form, true);

        return;
      }

      event.preventDefault();
      show();
      // The banner sits above the columns and the page can be long, so
      // bring it into view before moving focus — a message announced to
      // a screen reader and scrolled off for everyone else is half a
      // fix.
      //
      // Focus lands on the first column rather than on an offending one:
      // no single select is wrong here, the set is missing a field, and
      // the first is where an agent would start looking.
      error.scrollIntoView({ block: "center", behavior: "smooth" });
      selects[0].focus({ preventScroll: true });
    });

    selects.forEach((select) =>
      select.addEventListener("change", clearWhenSatisfied),
    );
  });
}
